import { useMemo } from "react";
import { panelStore, PanelViewsList } from "entities/Panel";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

type RequestFuncParams = object;

type SharedState = Partial<{
  filter: Partial<RequestFuncParams>;
  totalCount: number;
}>;

class PanelHistory<
  State extends {
    view: PanelViewsList;
    state: SharedState;
  },
> {
  private stateStack: State[] = [];

  public get state(): State[] {
    return this.stateStack;
  }

  public get length(): number {
    return this.stateStack.length;
  }

  public get currentState(): State | null {
    return this.last;
  }

  public get previous(): State | null {
    return this.stateStack.at(-2) ?? null;
  }

  public get last(): State | null {
    return this.stateStack.at(-1) ?? null;
  }

  public stateByView(view: PanelViewsList): State["state"] | null {
    const target = this.stateStack.find((state) => state.view === view);
    return target ? target.state : null;
  }

  public pushOrUpdateState(data: State): void {
    const targetStateIndex = this.stateStack.findIndex((state) => state.view === data.view);
    if (targetStateIndex === -1) {
      this.stateStack.push(data);
    } else {
      this.stateStack[targetStateIndex] = { ...data };
    }
  }

  public clear(): void {
    this.stateStack = [];
  }

  public back(): void {
    if (!this.stateStack.length) return;
    this.stateStack = this.stateStack.slice(0, this.stateStack.length - 1);
  }
}

const panelHistory = new PanelHistory();

type SetHistoryFunc = (state: SharedState) => void;

type UsePanelViewsReturnType = {
  currentView: PanelViewsList;
  clearViewHistory: () => void;
  showIntroView: () => void;
  showMainView: () => void;
  showOpenedCardView: () => void;

  showCardsByTagView: () => void;
  setCardsByTagViewHistory: SetHistoryFunc;

  showFilteredCardsView: () => void;
  setFilteredCardsViewHistory: SetHistoryFunc;

  showPreviousView: () => void;

  isCurrentViewIntro: boolean;
  isCurrentViewMain: boolean;
  isCurrentViewOpenedCard: boolean;
  isCurrentViewCardsByTag: boolean;
  isCurrentViewFilteredCards: boolean;

  currentHistoryState: SharedState | null;
};

export const usePanelViews = (): UsePanelViewsReturnType => {
  const currentView = useAppSelector(panelStore.selectors.currentView);
  const dispatch = useAppDispatch();

  const showIntroView = () => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.intro,
      state: { filter: {}, totalCount: 0 },
    });
    dispatch(panelStore.actions.setCurrentViewAction(PanelViewsList.intro));
  };

  const showMainView = () => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.main,
      state: { filter: {}, totalCount: 0 },
    });
    dispatch(panelStore.actions.setCurrentViewAction(PanelViewsList.main));
  };

  const showOpenedCardView = () => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.openedCard,
      state: { filter: {}, totalCount: 0 },
    });
    dispatch(panelStore.actions.setCurrentViewAction(PanelViewsList.openedCard));
  };

  const showCardsByTagView = () => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.cardsByTag,
      state: { filter: {}, totalCount: 0 },
    });
    dispatch(panelStore.actions.setCurrentViewAction(PanelViewsList.cardsByTag));
  };
  const setCardsByTagViewHistory: SetHistoryFunc = (state = { totalCount: 0 }) => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.cardsByTag,
      state,
    });
  };

  const showFilteredCardsView = () => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.filteredCards,
      state: { filter: {}, totalCount: 0 },
    });
    dispatch(panelStore.actions.setCurrentViewAction(PanelViewsList.filteredCards));
  };

  const setFilteredCardsViewHistory: SetHistoryFunc = (state = { totalCount: 0 }) => {
    panelHistory.pushOrUpdateState({
      view: PanelViewsList.filteredCards,
      state,
    });
  };

  const showPreviousView = () => {
    panelHistory.back();
    if (panelHistory.last) {
      dispatch(panelStore.actions.setCurrentViewAction(panelHistory.last.view));
    } else {
      showMainView();
    }
  };

  const clearViewHistory = () => {
    panelHistory.clear();
  };

  const currentHistoryState = useMemo<SharedState | null>(() => panelHistory.stateByView(currentView), [currentView]);

  const isCurrentViewIntro = useMemo(() => currentView === PanelViewsList.intro, [currentView]);
  const isCurrentViewMain = useMemo(() => currentView === PanelViewsList.main, [currentView]);
  const isCurrentViewOpenedCard = useMemo(() => currentView === PanelViewsList.openedCard, [currentView]);
  const isCurrentViewCardsByTag = useMemo(() => currentView === PanelViewsList.cardsByTag, [currentView]);
  const isCurrentViewFilteredCards = useMemo(() => currentView === PanelViewsList.filteredCards, [currentView]);

  return {
    currentView,
    clearViewHistory,
    showIntroView,
    showMainView,
    showOpenedCardView,
    //
    showCardsByTagView,
    setCardsByTagViewHistory,
    //
    showFilteredCardsView,
    setFilteredCardsViewHistory,

    showPreviousView,

    isCurrentViewIntro,
    isCurrentViewMain,
    isCurrentViewOpenedCard,
    isCurrentViewCardsByTag,
    isCurrentViewFilteredCards,

    currentHistoryState,
  };
};
